import { Close } from '@mui/icons-material';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import React, { useContext, useState } from 'react';
import { ErrorIcon } from '../../../vectors/index';
import { useBulkAction } from '@features/applications/hooks/useBulkAction';
import { toast } from 'react-toastify';
import { LoadingButton } from '@mui/lab';
import { authContext } from '@staffedupusa/shared-components';
import { useEnterpriseBulkAction } from '@features/applications/hooks/useEnterpriseBulkAction';

function BulkTeamActionModal({
  isOpen,
  handleClose,
  selectedApplicationIds,
  action,
  handleReset,
}: {
  isOpen: boolean;
  selectedApplicationIds: number[];
  handleClose: () => void;
  action: 'terminate' | 'resign' | 'team';
  handleReset?: () => void;
}) {
  const { authData } = useContext(authContext);
  const [errorText, setErrorText] = useState<string>('');

  const { createBulkAction, isCreateBulkActionLoading, resetCreateBulkAction } =
    useBulkAction({
      setErrorText,
    });
  const { enterpriseCreateBulkAction, isEnterpriseCreateBulkActionLoading } =
    useEnterpriseBulkAction({
      setErrorText,
    });
  const getTitle = (action: 'terminate' | 'resign' | 'team') => {
    switch (action) {
      case 'terminate':
        return `Termination Confirmation`;
      case 'resign':
        return `Resignation Confirmation`;
      case 'team':
        return `My Team Confirmation`;
      default:
        return 'action';
    }
  };
  const getBodyText = (action: 'terminate' | 'resign' | 'team') => {
    switch (action) {
      case 'terminate':
        return `Are you sure you want to terminate <b>${selectedApplicationIds.length}</b> employees?`;
      case 'resign':
        return `Are you sure you want to mark <b>${selectedApplicationIds.length}</b> <br/> employees as resigned?`;
      case 'team':
        return `Are you sure you want to add <b>${selectedApplicationIds.length}</b> employees to your team?  This will update your staff levels <br/> and allow you to communicate with these employees with the my team messenger.`;
      default:
        return 'action';
    }
  };

  const handleSubmit = () => {
    if (selectedApplicationIds.length === 0) {
      setErrorText('Please select at least one application.');
      return;
    }
    const options = {
      onSuccess: (data: { status: number }) => {
        if (data?.status === 200) {
          handleReset?.();
          toast.success(
            `Successfully ${action} ${selectedApplicationIds.length} applications.`,
          );
          resetCreateBulkAction();
          handleClose();
        }
      },
    };
    if (authData.user?.enterprise) {
      enterpriseCreateBulkAction(
        {
          action: action === 'terminate' ? 'TERMINATED' : 'RESIGNED',
          ids: selectedApplicationIds?.join(','),
        },
        options,
      );
    } else {
      createBulkAction(
        {
          action: action === 'terminate' ? 'TERMINATED' : 'RESIGNED',
          ids: selectedApplicationIds?.join(','),
        },
        options,
      );
    }
  };
  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        handleClose();
      }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={handleClose}
        >
          <Close />
        </IconButton>
        <br />
        <ErrorIcon />
        <br />
        <Typography variant="h4" mt={1}>
          {getTitle(action)}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {errorText && <Alert severity="error">{errorText}</Alert>}
        <Typography
          textAlign="center"
          variant="body1"
          mt={1}
          dangerouslySetInnerHTML={{
            __html: getBodyText(action),
          }}
        />

        <Stack spacing={2} mt={2} mb={2}>
          <LoadingButton
            loading={
              isCreateBulkActionLoading || isEnterpriseCreateBulkActionLoading
            }
            onClick={handleSubmit}
            variant="contained"
          >
            Yes I’m Sure
          </LoadingButton>
          <Button
            disabled={
              isCreateBulkActionLoading || isEnterpriseCreateBulkActionLoading
            }
            onClick={() => handleClose()}
            variant="outlined"
          >
            No Go Back
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

export default BulkTeamActionModal;
